import React from 'react';
import { get } from 'lodash';

import { useSelector } from '../../store/utils';
import { RelayControl } from '../../shared';
import { FormattedMessage } from 'react-intl';
import { SliderBox } from '../../molecules/slider-box';

type Props = {
  index: number;
};

export const RelaySettingsValue = ({ index }: Props) => {
  const relayControlType = useSelector((s) =>
    get(s.variables, `RELAY_CONTROL_TYPE[0][${index}]`)
  );

  const heatWaterAboveTemp =
    relayControlType === RelayControl.HeatWaterAboveTemp;
  const heatWaterBelowTemp =
    relayControlType === RelayControl.HeatWaterBelowTemp;
  const batteryAboveSOC = relayControlType === RelayControl.BatteryFull;
  const batteryBelowSOC = relayControlType === RelayControl.BatteryBelowSOC;
  const spotPriceAbove = relayControlType === RelayControl.SpotPriceAbove;
  const spotPriceBelow = relayControlType === RelayControl.SpotPriceBelow;
  const spotPriceAbove15M = relayControlType === RelayControl.SpotPriceAbove15M;
  const spotPriceBelow15M = relayControlType === RelayControl.SpotPriceBelow15M;

  return (
    <>
      {heatWaterAboveTemp && (
        <>
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.temp_relay"
                defaultMessage="Teplota bojleru pro sepnutí relé"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.temp_above_relay.subtitle"
                defaultMessage="Při teplotě bojleru nad tuto hodnotu dojde k sepnutí relé"
              />
            }
            variableId={`HW_ABOVE_TEMP_RELAY[${index}]`}
            max={85}
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 0,
            }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.temp_relay_hyst"
                defaultMessage="Hystereze bojleru pro rozepnutí relé"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.temp_above_relay_hyst.subtitle"
                defaultMessage="Pokud se teplota bojleru sníží o tuto hodnotu pod nastavenou pro sepnutí, dojde k rozepnutí relé"
              />
            }
            variableId={`HW_ABOVE_TEMP_RELAY_HYST[${index}]`}
            max={10}
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 0,
            }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
        </>
      )}
      {heatWaterBelowTemp && (
        <>
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.temp_relay"
                defaultMessage="Teplota bojleru pro sepnutí relé"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.temp_below_relay.subtitle"
                defaultMessage="Při teplotě bojleru pod tuto hodnotu dojde k sepnutí relé"
              />
            }
            variableId={`HW_BELOW_TEMP_RELAY[${index}]`}
            max={85}
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 0,
            }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.temp_below_relay_hyst"
                defaultMessage="Hystereze bojleru pro rozepnutí relé"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.temp_below_relay_hyst.subtitle"
                defaultMessage="Pokud se teplota bojleru zvýší o tuto hodnotu nad nastavenou pro sepnutí, dojde k rozepnutí relé"
              />
            }
            variableId={`HW_BELOW_TEMP_RELAY_HYST[${index}]`}
            max={10}
            opts={{
              style: 'unit',
              unit: 'celsius',
              maximumFractionDigits: 0,
            }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
        </>
      )}
      {batteryAboveSOC && (
        <>
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.above_soc_relay"
                defaultMessage="Nabití baterie pro sepnutí relé"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.above_soc_relay.subtitle"
                defaultMessage="Při nabití baterie nad tuto hodnotu dojde k sepnutí relé"
              />
            }
            variableId={`BAT_ABOVE_SOC_RELAY[${index}]`}
            max={100}
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.above_soc_relay_hyst"
                defaultMessage="Hystereze baterie pro rozepnutí relé"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.above_soc_relay_hyst.subtitle"
                defaultMessage="Pokud se nabití baterie sníží o tuto hodnotu pod nastavenou pro sepnutí, dojde k rozepnutí relé"
              />
            }
            variableId={`BAT_ABOVE_SOC_RELAY_HYST[${index}]`}
            max={60}
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
        </>
      )}
      {batteryBelowSOC && (
        <>
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.below_soc_relay"
                defaultMessage="Nabití baterie pro sepnutí relé"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.below_soc_relay.subtitle"
                defaultMessage="Při poklesu nabití baterie pod tuto hodnotu dojde k sepnutí relé"
              />
            }
            variableId={`BAT_BELOW_SOC_RELAY[${index}]`}
            max={100}
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.below_soc_relay_hyst"
                defaultMessage="Hystereze baterie pro rozepnutí relé"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.below_soc_relay_hyst.subtitle"
                defaultMessage="Pokud se nabití baterie zvýší o tuto hodnotu nad nastavenou pro sepnutí, dojde k rozepnutí relé"
              />
            }
            variableId={`BAT_BELOW_SOC_RELAY_HYST[${index}]`}
            max={60}
            opts={{ style: 'percent', maximumFractionDigits: 0 }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
        </>
      )}
      {spotPriceAbove && (
        <>
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.above_price_relay"
                defaultMessage="Cena spotu pro sepnutí relé"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.above_price_relay.subtitle"
                defaultMessage="Při cene spotu nad tuto hodnotu dojde k sepnutí relé. Pokud je cena nižší, dojde k rozepnutí relé."
              />
            }
            variableId={`SPOT_PRICE_ABOVE_RELAY[${index}]`}
            max={{ CZK: 10, EUR: 0.5 }}
            min={{ CZK: -10, EUR: -0.5 }}
            opts={{
              style: 'currency',
            }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
        </>
      )}
      {spotPriceBelow && (
        <>
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.below_price_relay"
                defaultMessage="Cena spotu pro sepnutí relé"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.below_price_relay.subtitle"
                defaultMessage="Při cene spotu pod tuto hodnotu dojde k sepnutí relé. Pokud je cena vyšší, dojde k rozepnutí relé."
              />
            }
            variableId={`SPOT_PRICE_BELOW_RELAY[${index}]`}
            max={{ CZK: 10, EUR: 0.5 }}
            min={{ CZK: -10, EUR: -0.5 }}
            opts={{
              style: 'currency',
            }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
        </>
      )}
      {spotPriceAbove15M && (
        <>
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.above_price_relay_15m"
                defaultMessage="Cena spotu pro sepnutí relé (15 min)"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.above_price_relay_15m.subtitle"
                defaultMessage="Při cene spotu nad tuto hodnotu dojde k sepnutí relé. Pokud je cena nižší, dojde k rozepnutí relé."
              />
            }
            variableId={`SPOT_PRICE_ABOVE_RELAY_15MIN[${index}]`}
            max={{ CZK: 10, EUR: 0.5 }}
            min={{ CZK: -10, EUR: -0.5 }}
            opts={{
              style: 'currency',
            }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
        </>
      )}
      {spotPriceBelow15M && (
        <>
          <SliderBox
            title={
              <FormattedMessage
                id="settings.relay.below_price_relay_15m"
                defaultMessage="Cena spotu pro sepnutí relé (15 min)"
              />
            }
            subtitle={
              <FormattedMessage
                id="settings.relay.below_price_relay_15m.subtitle"
                defaultMessage="Při cene spotu pod tuto hodnotu dojde k sepnutí relé. Pokud je cena vyšší, dojde k rozepnutí relé."
              />
            }
            variableId={`SPOT_PRICE_BELOW_RELAY_15MIN[${index}]`}
            max={{ CZK: 10, EUR: 0.5 }}
            min={{ CZK: -10, EUR: -0.5 }}
            opts={{
              style: 'currency',
            }}
            visibility={{ variableId: `RELAY_ENABLED[${index}]` }}
          />
        </>
      )}
    </>
  );
};
